import type { OnChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import { trackById } from '@freelancer/ui/helpers';
import { TEXT_COLOR_TAILWIND_VARIANTS } from '@freelancer/ui/text';
import escapeStringRegexp from 'escape-string-regexp';
import type { TextSection } from './highlight-text.types';
import { HighlightBackground } from './highlight-text.types';
import { AnchorScrollComponent } from '../anchor-scroll/anchor-scroll.component';
import { FontColor } from '../text';

@Component({
  standalone: true,
  imports: [AnchorScrollComponent],
  selector: 'fl-highlight-text',
  template: `
    @for (section of textSections; track trackById($index, section)) {
      @if (section.hasAnchor && anchorScrollName) {
        <fl-anchor-scroll
          [name]="anchorScrollName"
          [inline]="true"
        ></fl-anchor-scroll>
      }
      @if (section.text) {
        <span
          [class]="
            section.highlighted
              ? 'Highlighted ' +
                TEXT_COLOR_TAILWIND_VARIANTS[highlightedTextColor]
              : ''
          "
          [attr.data-background]="backgroundColor"
          >{{ section.text }}</span
        >
      }
    }
  `,
  styleUrls: ['./highlight-text.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HighlightTextComponent implements OnChanges {
  readonly TEXT_COLOR_TAILWIND_VARIANTS = TEXT_COLOR_TAILWIND_VARIANTS;
  trackById = trackById;

  @Input() text: string;
  @Input() highlight: string;
  @Input() backgroundColor = HighlightBackground.LIGHT;
  /** Name of the anchor scroll target to place on the first highlighted term. */
  @Input() anchorScrollName?: string;
  /** Colour to use for the text under the highlighted section. */
  @Input() highlightedTextColor = FontColor.INHERIT;
  @HostBinding('attr.data-truncate')
  @Input()
  truncate = false;
  /** Use inline display instead of inline-block. Allows floated siblings (e.g. timestamps) to sit beside the text. */
  @HostBinding('attr.data-inline')
  @Input()
  inline = false;

  textSections: TextSection[];

  private highlightTerms: string[];

  ngOnChanges(): void {
    if (!this.text || !this.highlight || this.highlight.trim() === '') {
      this.textSections = [{ text: this.text, highlighted: false }];
      return;
    }

    this.setHighlightTerms();

    const regexString = `(${this.highlightTerms
      .map(term => `\\b${term}`)
      .join('|')})`;
    const highlightRegex = new RegExp(regexString, 'i');

    let anchorPlaced = false;
    this.textSections = this.text
      .split(highlightRegex)
      .map((extract: string, index: number) => {
        const isHighlighted = !!extract.match(highlightRegex);
        // Place anchor on the first highlighted term
        const hasAnchor = isHighlighted && !anchorPlaced;
        if (hasAnchor) {
          anchorPlaced = true;
        }
        return {
          id: index,
          text: extract,
          highlighted: isHighlighted,
          hasAnchor,
        };
      })
      .filter(section => section.text !== '');
  }

  private setHighlightTerms(): void {
    const highlightTerm = escapeStringRegexp(this.highlight.trim());
    const individualHighlightTerms = highlightTerm.split(/\s+/);

    this.highlightTerms =
      individualHighlightTerms.length > 1
        ? [highlightTerm, ...individualHighlightTerms]
        : [highlightTerm];
  }
}
